// Every embed setting the marketing partner can control, parsed once from the
// iframe's own query string. Lives in its own module so both App.js and the
// converter page can read it without importing each other.
//
// The partner embeds the site as several independent iframes:
//   /                                      the converter
//   /?embed=auth                           the sign-in button alone
//   /?embed=plan&plan=pro&cycle=yearly     one checkout button per plan

const params = (() => {
  if (typeof window === 'undefined') return new URLSearchParams('');
  return new URLSearchParams(window.location.search || '');
})();

// Treat "1", "true" and a bare flag (?hideAuth) as on; anything else is off.
const flag = (name) => {
  const value = params.get(name);
  return value === '' || value === '1' || value === 'true';
};

const embedMode = params.get('embed') || '';

export const isAuthEmbed = embedMode === 'auth';
export const isPlanEmbed = embedMode === 'plan';
export const isButtonEmbed = isAuthEmbed || isPlanEmbed;

// ?authPopup=1 — this window is the sign-in popup opened by a standalone
// ?embed=auth button that had no converter frame to hand off to (see
// utils/authPopup.js). It renders the auth dialog alone, reports the session
// back to its opener, and closes.
export const isAuthPopup = flag('authPopup');
export const authPopupTab = params.get('tab') === 'signup' ? 'signup' : 'signin';

// ?checkout=<planId> on a sign-in popup — the visitor clicked a plan button
// while signed out, so this window signs them in and then continues to Stripe
// itself rather than closing. One window keeps the whole purchase on one user
// gesture; a second window.open after the await would be blocked. Read in
// AppContext's popup-side auth-complete effect.
export const authPopupCheckoutPlan = (isAuthPopup && params.get('checkout')) || '';

// Plan button configuration, read straight off the query string so the partner
// can change a button by editing its iframe src — no redeploy.
export const planEmbedConfig = {
  planId: params.get('plan') || 'starter',
  cycle: params.get('cycle') === 'yearly' ? 'yearly' : 'monthly',
  label: params.get('label') || '',
  // ?price=0 hides the price so the label stands alone.
  showPrice: params.get('price') !== '0',
  // ?fill=1 stretches the button to its iframe instead of sizing to its label,
  // for partners whose pricing cards use full-width CTAs. Off by default so
  // existing fixed-size button embeds keep their current dimensions.
  fill: flag('fill'),
  // ?thanks=/path/ sends the top window to a purchase-only page on the
  // partner's site once payment lands, giving their conversion tag a page load
  // to fire on — checkout runs in a popup, so their page never reloads by
  // itself. A PATH only; it is resolved against the parent's origin, never
  // taken as a URL. See buildPartnerReturnUrl in context/AppContext.js.
  thanksPath: params.get('thanks') || '',
};

// ?hideAuth=1 on the converter embed removes its header sign-in button, for
// partners who place the standalone ?embed=auth button in their own nav and
// would otherwise show two. The button is the header's only content, so this
// collapses the header entirely.
export const hideHeaderAuth = flag('hideAuth');
